import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Refined white card for ticket types and add-ons (accent edge, header, badges, rows).
 * Projected slots: [cardTitle], [cardPrice], [cardAside], [cardDescription], default slot for rows.
 * The accent is exposed as --choice-accent to the projected rows.
 */
@Component({
  selector: 'app-choice-card',
  templateUrl: './choice-card.component.html',
  styleUrls: ['./choice-card.component.scss'],
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--choice-accent]': 'accent',
    '[style.--choice-accent-text]': 'accentText'
  }
})
export class ChoiceCardComponent {
  // Ticket type color (null: primary)
  @Input() accent: string | null = null;
  // Readable text color on the accent
  @Input() accentText: string | null = null;
  @Input() showHeader: boolean = true;
  // Section title above the content (e.g. cart step)
  @Input() eyebrow: string | null = null;
  // Accent edge on the left of the card
  @Input() showAccentEdge: boolean = true;
  // Single choice card: the whole card toggles the selection and shows a check
  @Input() selectable: boolean = false;
  @Input() selected: boolean = false;
  @Input() soldOut: boolean = false;
  @Input() soldOutLabel: string = '';
  // Promotion text (e.g. complimentary offer)
  @Input() offer: string | null = null;
  @Input() remaining: number | null | undefined = null;
  @Output() activate = new EventEmitter<void>();

  get interactive(): boolean {
    return this.selectable && !this.soldOut;
  }

  // Few tickets left
  get showRemaining(): boolean {
    return !this.soldOut && !!this.remaining && this.remaining > 0 && this.remaining < 20;
  }

  onActivate(event?: Event): void {
    if (!this.interactive) return;
    // Links inside the description keep their own behavior
    if ((event?.target as HTMLElement | null)?.closest?.('a')) return;
    if (event?.type === 'keydown') event.preventDefault();
    this.activate.emit();
  }
}
